/**
 * FeedbackPage — data-bound Feedback tab (Plan 04-07 Task 2).
 *
 * Layout (IA F-03):
 *   - Feedback-specific controls row (sort, min/max rating, has-text, Clear).
 *   - <FeedbackTable> — dense sortable table (v1.4 SSOT milestone). Replaces
 *     the v1.3 card-list which was unusable at ~1.5k-row windows. Header
 *     clicks on Time / Rating toggle the server sort via `commitFeedback`.
 *
 * Global filters (preset / role / cohort / users / channel) come from the
 * always-visible FilterBar (rendered by DashboardLayout) → `useUrlFilters`.
 * Feedback-only URL state (sort / limit / offset / minRating / maxRating /
 * hasText) lives in `useFeedbackUrlState` — a sibling hook created to
 * preserve the `useUrlFilters.filtersEqual` fixed-keyset invariant
 * (WARNING-08).
 *
 * K1 (user report 2026-04): the local Apply button was a leftover from the
 * pre-live-apply world. The global FilterBar already live-applies every edit
 * (Bug F1); the Feedback-local controls now mirror that — every change flows
 * through `feedback.commit(patch)` (bypasses the pending buffer, writes to
 * the URL immediately, resets offset to 0). The Apply button is gone.
 *
 * K3 (user report 2026-04): the local "User" input was redundant with the
 * global FilterBar's users[] multi-select. The Input is removed here; the
 * Feedback endpoint already honors `filters.users` server-side (see
 * `computeFeedbackCards` — Zod-validated `users: z.array(z.string())`).
 * The `userId` URL key and `useFeedbackUrlState` field are preserved for
 * deep-link back-compat; the hook still forwards `feedback.userId` to the
 * API so legacy URLs keep working even without a visible control.
 *
 * Pagination: the hook fetches one page per `(filters, sort, limit, offset,
 * …)` tuple. We accumulate pages in a local array keyed on the filter
 * signature — every signature change resets the accumulator; every offset
 * advance appends new rows. Clear resets both hooks' pending + committed
 * state in one call.
 *
 * Each row's user link lives INSIDE <FeedbackCard> (already a react-router
 * <Link to={`/user/${userId}`}> on the username span) — wrapping the whole
 * card in another Link would nest anchors and fail a11y, so we don't.
 */

import { RecountNotice } from '@/components/RecountNotice';
import * as React from 'react';

import { FeedbackTable } from '@/components/FeedbackTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFeedbackCards, isInvalidRatingRange } from '@/hooks/use-feedback';
import { useFeedbackUrlState } from '@/hooks/use-feedback-url-state';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { filtersQuestion } from '@/lib/filters-schema';
import { cn } from '@/lib/utils';
import type {
  FeedbackCardRow,
  FeedbackSort,
  FeedbackSource,
} from '@/services/api-types';

// BUG-A (v1.4 dashboard-ux-fix): the dedicated Sort dropdown was removed from
// the Feedback controls row. Sorting is now driven exclusively by clicking a
// sortable column header in `<FeedbackTable>` (Time / Rating / Latency /
// Cost), which live-commits the new sort via `commitFeedback({ sort, offset:
// 0 })`. The `sort` URL param and `feedback.sort` state are preserved — only
// the redundant control is gone. See Bug B for the companion fix that makes
// Latency/Cost column clicks actually stick.

// K2 — three-way source segmented control. `all` is the default (no predicate),
// `chat` excludes `name='scheduled_brief'`, `brief` restricts to it.
const SOURCE_OPTIONS: { value: FeedbackSource; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'chat', label: 'Chat' },
  { value: 'brief', label: 'Brief' },
];

// ---------------------------------------------------------------------------
// Controls row — bound to pendingFilters + feedback.pending
// ---------------------------------------------------------------------------

function ErrorRetry({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card data-testid={`${label}-error`}>
      <CardContent className="p-4 space-y-2">
        <div className="text-sm font-medium text-red-600">
          {label} failed to load
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs underline text-primary hover:no-underline"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

/**
 * Build a stable string signature from every param that re-keys the server
 * response. When the signature changes we RESET the accumulated-rows array;
 * when only `offset` changes we APPEND. JSON.stringify on stripDefaults
 * avoids false resets on cosmetic URL changes (default-valued keys).
 */
/** WHICH rows are being asked for — sort and page size included, because this
 *  endpoint returns a PAGE. A re-sort brings back the first N rows in the new
 *  order: a different SET of traces, not the same ones rearranged (owner's
 *  ruling 3, 2026-08-20), so the list blanks for it exactly as for a filter. */
function buildSignature(
  filtersKey: unknown,
  sort: FeedbackSort,
  limit: number,
  minRating: number | undefined,
  maxRating: number | undefined,
  hasText: boolean | undefined,
  userId: string | undefined,
  source: FeedbackSource,
): string {
  return JSON.stringify({
    f: filtersKey,
    sort,
    limit,
    minRating: minRating ?? null,
    maxRating: maxRating ?? null,
    hasText: hasText ?? null,
    userId: userId ?? null,
    source,
  });
}

export function FeedbackPage(): React.ReactElement {
  const filtersHook = useUrlFilters();
  const feedback = useFeedbackUrlState();

  const {
    filters,
    reset: resetFilters,
  } = filtersHook;
  const {
    // K1 — live-apply: controls bind directly to committed state + write via
    // `commitFeedback`. The legacy `pending` buffer is retained by the hook
    // for tests/back-compat but no longer consumed here.
    reset: resetFeedback,
    setOffset,
    commit: commitFeedback,
    // Phase 5 (dashboard-trace-ux) — inline expand state. Owned by the URL
    // (`?expanded=tid_a,tid_b`, capped at 10 by the hook) so deep-links
    // reproduce the user's expanded layout on reload.
    setExpanded,
  } = feedback;

  // BUG-2 (user report 2026-04): previously the `Best` sort coerced
  // `hasText=true` inside the fetch AND the checkbox mirrored both the
  // sort and the user's hasText toggle (showing a phantom checkmark and
  // silently mutating the filter when sorting by rating). That coupled
  // two independent controls — sort order and the text-only predicate —
  // and made the UX feel like filtering flipped on its own. The sort and
  // `hasText` are now fully independent: the checkbox reflects exactly
  // what the user chose, and the fetch forwards `feedback.hasText`
  // verbatim. If a user wants only rated rows with text feedback
  // alongside the Best sort, they toggle the checkbox themselves.

  // P1-1 (v1.4-dashboard-ssot): surface an inverted rating range before we
  // ever ask the hook to fetch. The hook also refuses to fire (`enabled:
  // !invalidRange`), but the flag is computed once here so the UI can show
  // an inline hint on the filter row AND skip rendering the generic
  // ErrorRetry card. Keeps the user in the filter-fix loop instead of
  // bouncing them to a "failed to load" surface that retried the same 400.
  const invalidRange = isInvalidRatingRange(
    feedback.minRating,
    feedback.maxRating,
  );

  // Fetch one page for the CURRENT committed tuple.
  const query = useFeedbackCards(filters, {
    sort: feedback.sort,
    limit: feedback.limit,
    offset: feedback.offset,
    minRating: feedback.minRating,
    maxRating: feedback.maxRating,
    hasText: feedback.hasText,
    userId: feedback.userId,
    // K2 — source partition flows through to the API.
    source: feedback.source,
  });

  // Pagination accumulator. Rows are appended when the offset advances
  // without changing the signature; any signature change resets to the
  // fresh page's rows.
  const signature = React.useMemo(
    () =>
      buildSignature(
        filtersQuestion(filters),
        feedback.sort,
        feedback.limit,
        feedback.minRating,
        feedback.maxRating,
        feedback.hasText,
        feedback.userId,
        feedback.source,
      ),
    [
      filters,
      feedback.sort,
      feedback.limit,
      feedback.minRating,
      feedback.maxRating,
      feedback.hasText,
      feedback.userId,
      feedback.source,
    ],
  );

  const [accumulated, setAccumulated] = React.useState<FeedbackCardRow[]>([]);
  const [accumulatedSignature, setAccumulatedSignature] = React.useState<
    string | null
  >(null);
  const [accumulatedOffset, setAccumulatedOffset] = React.useState<number>(-1);
  /* The row COUNT for the accumulated signature. It is a property of the
     filter set, not of the page: Load-more changes the offset, which re-keys
     the query and blanks `data`, and reading the total off `data` there
     printed "Showing 25 of 0" for the length of every Load-more. */
  const [accumulatedTotal, setAccumulatedTotal] = React.useState(0);

  React.useEffect(() => {
    if (!query.isSuccess || query.data === undefined) return;
    // BUG — first-click "requires two clicks to refresh" (user report 2026-04):
    // stamping `accumulatedSignature` with the NEW signature while holding the
    // PREVIOUS filter's rows makes the follow-up run take the
    // `signature === accumulatedSignature` fast path, so the fresh rows never
    // reach the accumulator and the list stays frozen until the next
    // signature change.
    //
    // The `isPlaceholderData` guard is STILL load-bearing after the recount
    // rule, and for a narrower reason than before it: while a request is in
    // flight the hook hands this page nothing at all, so the early return
    // above fires first — but a DISABLED query (`enabled: !invalidRange`)
    // keeps its placeholder payload with nothing being fetched, and
    // `use-filtered-query.ts` deliberately leaves that state alone. Without
    // this line, inverting the rating range stamps the previous filter's rows
    // under the new signature, which is the original two-clicks bug.
    if (signature !== accumulatedSignature) {
      if (query.isPlaceholderData) return;
      setAccumulated(query.data.rows);
      setAccumulatedSignature(signature);
      setAccumulatedOffset(feedback.offset);
      setAccumulatedTotal(query.data.total);
      return;
    }
    // Same signature, same offset — already rendered; nothing to do.
    if (feedback.offset === accumulatedOffset) return;
    // Same signature, NEW offset — append. Guard against React StrictMode
    // double-invocation by requiring the offset advanced, which is the only
    // path Load-more can produce.
    if (feedback.offset > accumulatedOffset && !query.isPlaceholderData) {
      setAccumulated((prev) => [...prev, ...query.data.rows]);
      setAccumulatedOffset(feedback.offset);
      setAccumulatedTotal(query.data.total);
    }
  }, [
    query.isSuccess,
    query.data,
    query.isPlaceholderData,
    signature,
    accumulatedSignature,
    accumulatedOffset,
    feedback.offset,
  ]);

  /* The accumulator is only refilled once rows for THIS question land, so
     between a filter change and that moment it still holds the previous
     selection's rows — and the table's own skeleton gate is `isLoading &&
     rows.length === 0`, which those stale rows defeat. The QUESTION
     comparison is this page's own version of `isPlaceholderData`: true
     exactly while what is in hand answers a different question, and false for
     a Load-more, for a re-sort, and for a background refetch — none of which
     may blank the list. */
  const recounting = signature !== accumulatedSignature;
  /* …and the one window where "not this filter's answer" does NOT mean "a
     recount is running": the hook is DISABLED (an inverted rating range), so
     it is holding the previous filter's payload with nothing in flight and
     nothing coming. A skeleton there would wait forever for a request nobody
     made; the inline hint above the table owns that explanation instead.

     All THREE terms, the same three `use-filtered-query.ts` takes — a paused
     (offline) fetch also reports `isFetching: false`, and a two-term copy of
     this test here made the table print its "No data" empty state, a concrete
     answer of nothing, for as long as the connection was down. Two definitions
     of "nothing is coming" is one too many.

     …and `invalidRange` on its own, because a query disabled from the START
     has no placeholder to hold: on a deep link to an inverted range the three
     terms above are all false, and the page waited forever, under "not ready
     yet", for a request it had itself decided never to make. */
  const stranded =
    invalidRange ||
    (query.isPlaceholderData && !query.isFetching && !query.isPaused);
  const counting = !stranded && (query.isLoading || recounting);
  const rows = recounting ? [] : accumulated;
  /* Falls back to the count that came with these rows, for the Load-more
     window where the query is re-keyed by offset alone: the filter set has not
     changed, so its total has not either — only the page in flight has. */
  const total = recounting ? 0 : query.data?.total ?? accumulatedTotal;

  // ---------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------

  const handleClear = React.useCallback(() => {
    resetFilters();
    resetFeedback();
  }, [resetFilters, resetFeedback]);

  const handleLoadMore = React.useCallback(() => {
    setOffset(feedback.offset + feedback.limit);
  }, [feedback.offset, feedback.limit, setOffset]);

  return (
    <section aria-labelledby="feedback-heading" className="space-y-6">
      <header>
        <h1 id="feedback-heading" className="text-2xl font-semibold">
          Feedback
        </h1>
        <p className="text-sm text-muted-foreground">
          Rated traces — sort by triage to surface lowest ratings first.
        </p>
        {/* `recounting` IS this page's `isPlaceholderData`: what is in hand
            belongs to a different filter set. Passed as such so the line says
            "counted again" rather than "not ready yet". */}
        {/* `recounting` is true on the very first render too — the
            accumulator has no signature yet — and there is nothing to count
            AGAIN on a cold tab, so the "again" half is gated on having
            counted once. */}
        <RecountNotice
          queries={[
            {
              // `recounting` as well as `counting`: a Load-more is also a
              // request in flight, but it takes nothing off the screen — the
              // rows and the count stay — so announcing it would be false.
              isLoading: counting && recounting,
              isPlaceholderData: recounting && accumulatedSignature !== null,
            },
          ]}
        />
      </header>

      {/* Feedback-specific controls row (IA F-03). Global filters live in
          the always-visible FilterBar above this page. */}
      <div
        className="flex flex-wrap items-end gap-4 p-4 border rounded-md bg-card"
        data-testid="feedback-controls"
      >
        {/* K2 — Source segmented control (All / Chat / Brief). Live-applies via
            `commitFeedback`, same as every other control on this row. */}
        <div
          className="flex items-center gap-2"
          data-testid="feedback-source"
          role="group"
          aria-label="Feedback source"
        >
          <Label className="text-sm">Source:</Label>
          <div className="inline-flex rounded-md border overflow-hidden">
            {SOURCE_OPTIONS.map((o) => {
              const selected = feedback.source === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  data-testid={`feedback-source-${o.value}`}
                  aria-pressed={selected}
                  onClick={() => commitFeedback({ source: o.value })}
                  className={cn(
                    'px-3 py-1.5 text-sm transition-colors',
                    'border-r last:border-r-0',
                    selected
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-accent',
                  )}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* BUG-A (v1.4): the Sort dropdown was removed here. Sorting is now
            done exclusively via column-header clicks on `<FeedbackTable>` —
            the dropdown was redundant with the header affordance and the
            two controls drifting apart was a papercut. `feedback.sort`
            stays in the URL / hook state so deep-links still work. */}

        {/* P2-4 (v1.4): `step=1` alongside min/max nudges native up/down widgets
            to integer steps; the URL-state layer drops out-of-range values on
            both read and write so typing 99 silently reverts instead of
            persisting a dirty param. P1-1: `aria-invalid` when the range is
            inverted so AT users notice the validation state alongside the
            inline hint rendered below. */}
        <div className="flex items-center gap-2">
          <Label className="text-sm" htmlFor="feedback-min-rating">
            Min ★:
          </Label>
          <Input
            id="feedback-min-rating"
            data-testid="feedback-min-rating"
            type="number"
            min={1}
            max={5}
            step={1}
            aria-invalid={invalidRange || undefined}
            className="w-[80px]"
            value={feedback.minRating ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              commitFeedback({
                minRating: v === '' ? undefined : Number.parseInt(v, 10),
              });
            }}
          />
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm" htmlFor="feedback-max-rating">
            Max ★:
          </Label>
          <Input
            id="feedback-max-rating"
            data-testid="feedback-max-rating"
            type="number"
            min={1}
            max={5}
            step={1}
            aria-invalid={invalidRange || undefined}
            className="w-[80px]"
            value={feedback.maxRating ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              commitFeedback({
                maxRating: v === '' ? undefined : Number.parseInt(v, 10),
              });
            }}
          />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={feedback.hasText === true}
            onCheckedChange={(v) =>
              commitFeedback({ hasText: v === true ? true : undefined })
            }
            data-testid="feedback-has-text"
          />
          <span>Has text</span>
        </label>

        {/* Mobile-only sort control. The column-header sort buttons for
            Latency / Cost live inside `hidden sm:table-cell` headers, so on
            narrow viewports there was no way to reorder by those metrics.
            Desktop keeps header-click sort only (the deliberately removed
            duplicate dropdown — BUG-A above — stays gone). */}
        <div className="flex items-center gap-2 sm:hidden">
          <Label className="text-sm" htmlFor="feedback-mobile-sort">
            Sort:
          </Label>
          <select
            id="feedback-mobile-sort"
            data-testid="feedback-mobile-sort"
            className="h-9 rounded-md border bg-background px-2 text-sm"
            value={feedback.sort ?? 'newest'}
            onChange={(e) =>
              commitFeedback({
                sort: e.target.value as typeof feedback.sort,
                offset: 0,
              })
            }
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="triage">Triage (worst first)</option>
            <option value="best">Best first</option>
            <option value="latency_desc">Latency (high → low)</option>
            <option value="latency_asc">Latency (low → high)</option>
            <option value="cost_desc">Cost (high → low)</option>
            <option value="cost_asc">Cost (low → high)</option>
          </select>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleClear}
            data-testid="feedback-clear"
          >
            Clear
          </Button>
        </div>

        {/* P1-1 (v1.4-dashboard-ssot): inline hint for inverted rating range.
            `w-full` forces the message onto its own wrapped row within the
            `flex-wrap` container so it doesn't fight the rating inputs for
            horizontal space. Rendered inside the controls card so a screen
            reader walking the form lands on it right after the invalid
            inputs. */}
        {invalidRange && (
          <div
            role="alert"
            data-testid="feedback-range-hint"
            className="w-full text-xs text-red-600"
          >
            Min rating must be less than or equal to Max rating.
          </div>
        )}
      </div>

      {/* Sortable table — replaces the v1.3 card list (user report: the list
          was "not usable" at ~1.5k-row windows). Column headers `Time` and
          `Rating` toggle the server sort via `commitFeedback({ sort, offset: 0 })`
          so the accumulator resets and the fresh page reflects the new order. */}
      <TabErrorBoundary name="feedback">
        {/* P1-1: suppress the generic ErrorRetry card when the rating range
            is inverted — the hook skipped the fetch (so any `isError` left
            over from a previous tuple is stale noise), and the inline hint
            above already tells the user how to fix it. Retrying would just
            replay the same 400. */}
        {query.isError && !invalidRange ? (
          <ErrorRetry
            label="Feedback cards"
            onRetry={() => void query.refetch()}
          />
        ) : (
          <FeedbackTable
            rows={rows}
            total={total}
            sort={feedback.sort}
            onSortChange={(next) =>
              commitFeedback({ sort: next, offset: 0 })
            }
            /* `recounting` as well as the query's own flag: the frame in
               which fresh rows have arrived but the accumulator effect has
               not yet run would otherwise render an empty table with a real
               total ("Showing 0 of 25"). */
            isLoading={counting}
            /* `isLoading`, not `isFetching`: offline the fetch is PAUSED, so
               `isFetching` is false and the button stayed live — every further
               click walked the offset past a request that had not run, and on
               reconnect only the last page landed. The pages in between were
               never fetched and never missed. */
            isFetchingMore={query.isLoading && rows.length > 0}
            onLoadMore={handleLoadMore}
            expandedTraceIds={feedback.expanded}
            onExpandedChange={setExpanded}
          />
        )}
      </TabErrorBoundary>
    </section>
  );
}
